import { useEffect, useMemo } from 'react';
import type React from 'react';
import { withEscKeyPress } from '../utils';

type UseOnEscPressParams = React.KeyboardEvent<Element>;

/**
 * Re-usable hook for managing escape-press.
 * Event is managed through the onEscPress parameter, which is an event callback.
 * @param onEscPress Event handler for onEscPress
 *
 * @example
 * import useOnEscPress from './hooks';
 *
 * const MyComponent = () => {
 *   useOnEscPress((e) => { some logic });
 *   return (
 *     <div />
 *   );
 * };
 */
const useOnEscPress = (onEscPress: (event: UseOnEscPressParams) => void) => {
  const handleKeyDown = useMemo(() => withEscKeyPress(onEscPress), [onEscPress]);

  useEffect(() => {
    //  We want to remove any previous event liseners.
    document.removeEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};

export default useOnEscPress;
